import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { I18nProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/lib/auth";
import { FavoritesProvider } from "@/lib/favorites";
import { FEATURES } from "@/lib/features";
import Layout from "@/components/Layout";
import Ga4PageViews from "@/lib/ga4";
import { TrafficSourceTracker } from "@/lib/useTrafficSource";

import HomePage from "@/pages/HomePage";
import MarketplacePage from "@/pages/MarketplacePage";
import VehicleDetailPage from "@/pages/VehicleDetailPage";
import { LoginPage, RegisterPage } from "@/pages/AuthPages";
import JoinChooserPage from "@/pages/JoinChooserPage";
import TikTokCampaignPage from "@/pages/TikTokCampaignPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import InviteAcceptPage from "@/pages/InviteAcceptPage";
import DashboardPage from "@/pages/DashboardPage";
import FinancingPage from "@/pages/FinancingPage";
import InsurancePage from "@/pages/InsurancePage";
import InspectionPage from "@/pages/InspectionPage";
import DeliveryPage from "@/pages/DeliveryPage";
import ProviderDirectoryPage from "@/pages/ProviderDirectoryPage";
import OtherServicesPage from "@/pages/OtherServicesPage";
import EscrowTermsPage from "@/pages/EscrowTermsPage";
import ShowroomsPage from "@/pages/ShowroomsPage";
import AuctionsPage from "@/pages/AuctionsPage";
import { PartsPage, ServicesPage } from "@/pages/PartsServicesPages";
import {
  RentalsPage, SalvagePage, ExportPage, HeavyPage, MotorcyclesPage, MarinePage, FleetPage,
} from "@/pages/ModulePages";
import PricingPage from "@/pages/PricingPage";
import { TrustSafetyPage } from "@/pages/TrustDisputePages";
import { TermsPage, PrivacyPage, DisclaimerPage, RefundPolicyPage, FraudPolicyPage } from "@/pages/LegalPages";
import SellPage from "@/pages/SellPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import MessagesPage from "@/pages/MessagesPage";
import FavoritesPage from "@/pages/FavoritesPage";
import SavedSearchesPage from "@/pages/SavedSearchesPage";
import AdminPage from "@/pages/AdminPage";
import AdvertisePage from "@/pages/AdvertisePage";
import AdvertiserDashboard from "@/pages/AdvertiserDashboard";
import AdvertiserNewCampaign from "@/pages/AdvertiserNewCampaign";
import AdvertiserCheckoutSuccess from "@/pages/AdvertiserCheckoutSuccess";
import ServicesDirectoryPage from "@/pages/ServicesDirectoryPage";
import PartnerDetailPage from "@/pages/PartnerDetailPage";
import SellerPublicPage from "@/pages/SellerPublicPage";
import MoyasarCheckoutPage from "@/pages/MoyasarCheckoutPage";
import PaymentResultPage from "@/pages/PaymentResultPage";
import PaymentsAdminPage from "@/pages/admin/PaymentsAdminPage";
import PayoutAccountsPage from "@/pages/PayoutAccountsPage";
import PaymentHistoryPage from "@/pages/PaymentHistoryPage";
import PayoutsAdminPage from "@/pages/admin/PayoutsAdminPage";
import PartnerRegisterPage from "@/pages/PartnerRegisterPage";
import PartnerDashboardPage from "@/pages/PartnerDashboardPage";
import DealCompletionPage from "@/pages/DealCompletionPage";
import MyOrdersPage from "@/pages/MyOrdersPage";
import KycPage from "@/pages/KycPage";
import DisputesListPage, { NewDisputePage, DisputeDetailPage } from "@/pages/DisputesPages";
import NotificationsPage, { NotificationSettingsPage } from "@/pages/NotificationsPage";
import AccountSettingsPage from "@/pages/AccountSettingsPage";
import SellerProfilePage from "@/pages/SellerProfilePage";
import WriteReviewPage from "@/pages/WriteReviewPage";
import EditListingPage from "@/pages/EditListingPage";
import BoostListingPage from "@/pages/BoostListingPage";
import MyListingsPage from "@/pages/MyListingsPage";
import GiftInvitePage from "@/pages/GiftInvitePage";
import GiftPlansInvitePage from "@/pages/GiftPlansInvitePage";
import SnapAdLandingPage from "@/pages/SnapAdLandingPage";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import CityOnboardingModal from "@/components/CityOnboardingModal";
import ContactPage from "@/pages/ContactPage";
import FaqPage from "@/pages/FaqPage";

// Phase 3 — Business workspaces (additive, do not touch /dashboard)
import BusinessLandingPage from "@/pages/business/BusinessLandingPage";
import BusinessDashboardPage from "@/pages/business/BusinessDashboardPage";
import BusinessRegisterPage from "@/pages/business/BusinessRegisterPage";
import MyBusinessesPage from "@/pages/business/MyBusinessesPage";

// Phase 4 — Public company profiles + directory
import PublicCompanyPage from "@/pages/company/PublicCompanyPage";
import CompanyDirectoryPage from "@/pages/company/CompanyDirectoryPage";

// Phase 7 — Live Auctions, Escrow Transactions (additive, parallel to legacy)
import AuctionsV2Page from "@/pages/auctionsV2/AuctionsV2Page";
import AuctionDetailV2Page from "@/pages/auctionsV2/AuctionDetailV2Page";
import { TransactionsListPage, TransactionDetailPage } from "@/pages/TransactionsPages";

// Phase 8 — AI Assistant + Platform Analytics
// (AiAssistantButton is available as a component for pages that want to embed
// it; not injected globally to preserve the existing AiHelpButton in Layout.)
import PlatformAnalyticsPage from "@/pages/admin/PlatformAnalyticsPage";
import { RouteSeo } from "@/components/RouteSeo";


/**
 * iter218 — Business-member routing shield.
 *
 * A user invited into a business through an employee-invite link has
 * NO personal identity in CARTAL: no individual KYC, no personal
 * listings, no personal favorites/messages/orders/dashboard, no
 * individual verification banner, no upgrade to individual. Their
 * entire operational surface is their business dashboard, gated by
 * the RBAC that business assigns.
 *
 * This one guard wraps every personal-surface route (see App.js
 * routing table): `/dashboard`, `/verify`, `/kyc`, `/sell`,
 * `/favorites`, `/messages`, `/saved-searches`, `/orders`.
 *
 * Behaviour:
 *   • Unauthenticated user   → let the child render; each child
 *                               already has its own "please sign in"
 *                               state. Nothing changes for them.
 *   • account_type === "member" OR role === "business_member"
 *                             → hard-redirect to `/businesses`
 *                               (My Spaces). This is their working
 *                               entry point and lists the businesses
 *                               they belong to.
 *   • Anyone else             → render the child page unchanged.
 *
 * This is a pure client-side gate. The backend endpoints that back
 * each surface (KYC, sell-vehicle, orders, favorites, etc.) enforce
 * the same rule server-side, so an API-level bypass is impossible.
 */
function IndividualOnlyRoute({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isMember = user && (
    user.account_type === "member" || user.role === "business_member"
  );
  if (isMember) {
    // iter220 — /sell is intentionally NOT gated for members anymore.
    // A member must reach the sell form via any entry point that
    // exists today (dashboard quick-action, /my-listings "إعلان جديد",
    // header "أضف إعلانك", etc). Once inside the form:
    //   • the "بصفتي فرد" toggle is hidden for members (SellPage),
    //   • the backend rejects a submit with no business_id (403
    //     member_cannot_list_as_individual).
    // So the two-layer "no personal listing" rule still holds without
    // this route-level redirect breaking the entry flow for members.
    if (pathname === "/sell") return children;
    return <Navigate to="/businesses" replace />;
  }
  return children;
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <FavoritesProvider>
          <BrowserRouter>
            <Ga4PageViews />
            <TrafficSourceTracker />
            {/* iter167 — Nudge toasts below the sticky app header + the
                iOS/Android top safe area so they don't collide with the
                status bar on mobile. `offset` accepts CSS calc so it
                honors env(safe-area-inset-top) automatically. Position,
                theme, colours, animation, duration, and behaviour are
                otherwise unchanged. */}
            <Toaster
              theme="dark"
              position="top-center"
              richColors
              offset="calc(env(safe-area-inset-top, 0px) + 76px)"
              mobileOffset="calc(env(safe-area-inset-top, 0px) + 76px)"
            />
            <PWAInstallPrompt />
            {/* iter241 — one-shot post-registration city-onboarding
                modal. Self-gated: only renders for signed-in users
                with no city on file AND when the register flow set
                the pending flag. */}
            <CityOnboardingModal />
            <RouteSeo />
            <Routes>
              {/* iter307 — TikTok campaign landing page. Rendered
                  OUTSIDE the Layout wrapper so there's no app header
                  / footer chrome — the visitor lands in a full-screen
                  cinematic ad, which is what a TikTok bio link
                  destination should feel like. */}
              <Route path="/tiktok" element={<TikTokCampaignPage />} />
              <Route element={<Layout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/marketplace" element={<MarketplacePage />} />
                <Route path="/seller/:id" element={<SellerPublicPage />} />
                <Route path="/checkout/moyasar" element={<MoyasarCheckoutPage />} />
                <Route path="/payment/result" element={<PaymentResultPage />} />
                <Route path="/admin/payments" element={<PaymentsAdminPage />} />
                <Route path="/payouts" element={<PayoutAccountsPage />} />
                <Route path="/payments/history" element={<PaymentHistoryPage />} />
                <Route path="/admin/payouts" element={<PayoutsAdminPage />} />
                <Route path="/vehicle/:id" element={<VehicleDetailPage />} />
                <Route path="/vehicles/:id" element={<VehicleDetailPage />} />
                <Route path="/vehicle/:id/edit" element={<EditListingPage />} />
                <Route path="/vehicles/:id/edit" element={<EditListingPage />} />
                <Route path="/vehicle/:id/boost" element={<BoostListingPage />} />
                <Route path="/vehicles/:id/boost" element={<BoostListingPage />} />
                {/* Seller's central listings-manager (edit / boost / delete). */}
                <Route path="/my-listings" element={<MyListingsPage />} />
                <Route path="/my-ads" element={<Navigate to="/my-listings" replace />} />
                <Route path="/listings/manage" element={<Navigate to="/my-listings" replace />} />
                {/* Launch promo — shareable named-invite card. */}
                <Route path="/gift" element={<GiftInvitePage />} />
                <Route path="/invite/accept" element={<InviteAcceptPage />} />
                <Route path="/gift/plans" element={<GiftPlansInvitePage />} />
                <Route path="/gift-plans" element={<Navigate to="/gift/plans" replace />} />
                <Route path="/snap" element={<SnapAdLandingPage />} />
                <Route path="/ad/snap" element={<Navigate to="/snap" replace />} />
                <Route path="/invite" element={<Navigate to="/gift" replace />} />
                <Route path="/vehicle-history" element={<ProviderDirectoryPage category="vehicle_history" />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/faq/:category" element={<FaqPage />} />
                <Route path="/help" element={<FaqPage />} />
                <Route path="/support" element={<FaqPage />} />
                <Route path="/rentals" element={<RentalsPage />} />
                <Route path="/auctions" element={FEATURES.AUCTIONS_ENABLED ? <AuctionsPage /> : <Navigate to="/marketplace" replace />} />
                <Route path="/financing" element={<ProviderDirectoryPage category="financing" />} />
                <Route path="/insurance" element={<ProviderDirectoryPage category="insurance" />} />
                <Route path="/periodic-inspection" element={<ProviderDirectoryPage category="periodic_inspection" />} />
                <Route path="/pre-purchase-inspection" element={<ProviderDirectoryPage category="pre_purchase_inspection" />} />
                {/* legacy /inspection alias → defaults to mandatory periodic */}
                <Route path="/inspection" element={<ProviderDirectoryPage category="periodic_inspection" />} />
                <Route path="/delivery" element={<ProviderDirectoryPage category="delivery" />} />
                <Route path="/ownership-transfer" element={<ProviderDirectoryPage category="ownership_transfer" />} />
                {/* iter250 — "خدمات أخرى" hub + its 2 new sub-categories.
                    /delivery already exists above and is reused for the
                    transport & shipping section. */}
                <Route path="/other-services" element={<OtherServicesPage />} />
                <Route path="/other-services/:category" element={<ProviderDirectoryPage />} />
                <Route path="/car-wash" element={<ProviderDirectoryPage category="car_wash" />} />
                <Route path="/workshop" element={<ProviderDirectoryPage category="workshop" />} />
                <Route path="/escrow-terms" element={<EscrowTermsPage />} />
                <Route path="/showrooms" element={<ShowroomsPage />} />
                {/* Legacy wizard pages — kept for direct links but not in nav */}
                <Route path="/legacy/financing" element={<FinancingPage />} />
                <Route path="/legacy/insurance" element={<InsurancePage />} />
                <Route path="/legacy/inspection" element={<InspectionPage />} />
                <Route path="/legacy/delivery" element={<DeliveryPage />} />
                <Route path="/parts" element={<PartsPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/salvage" element={<SalvagePage />} />
                <Route path="/export" element={<ExportPage />} />
                <Route path="/heavy" element={<HeavyPage />} />
                <Route path="/motorcycles" element={<MotorcyclesPage />} />
                <Route path="/marine" element={<MarinePage />} />
                <Route path="/fleet" element={<FleetPage />} />
                <Route path="/disputes" element={<DisputesListPage />} />
                <Route path="/disputes/new" element={<NewDisputePage />} />
                <Route path="/disputes/:id" element={<DisputeDetailPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/settings/notifications" element={<NotificationSettingsPage />} />
                <Route path="/settings/account" element={<AccountSettingsPage />} />
                <Route path="/sellers/:id" element={<SellerProfilePage />} />
                <Route path="/sellers/:id/review" element={<WriteReviewPage />} />
                <Route path="/trust" element={<TrustSafetyPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/sell" element={<IndividualOnlyRoute><SellPage /></IndividualOnlyRoute>} />
                <Route path="/dashboard" element={<IndividualOnlyRoute><DashboardPage /></IndividualOnlyRoute>} />
                <Route path="/admin" element={<AdminPage />} />
                {/* Phase 3 — Business Workspaces (additive) */}
                <Route path="/business" element={<BusinessLandingPage />} />
                <Route path="/businesses" element={<MyBusinessesPage />} />
                <Route path="/businesses/new" element={<BusinessRegisterPage />} />
                <Route path="/business/:businessId" element={<BusinessDashboardPage />} />
                <Route path="/business/:businessId/:section" element={<BusinessDashboardPage />} />
                {/* Phase 4 — Public company profiles + directory (additive) */}
                <Route path="/companies" element={<CompanyDirectoryPage />} />
                <Route path="/company/:slug" element={<PublicCompanyPage />} />
                <Route path="/company/:slug/reviews" element={<PublicCompanyPage />} />
                {/* Phase 7 — Auctions v2 + Escrow transactions (additive) */}
                <Route path="/auctions-v2" element={FEATURES.AUCTIONS_ENABLED ? <AuctionsV2Page /> : <Navigate to="/marketplace" replace />} />
                <Route path="/auctions-v2/:id" element={FEATURES.AUCTIONS_ENABLED ? <AuctionDetailV2Page /> : <Navigate to="/marketplace" replace />} />
                <Route path="/transactions" element={<TransactionsListPage />} />
                <Route path="/transactions/:id" element={<TransactionDetailPage />} />
                {/* Phase 8 — AI + Analytics (additive) */}
                <Route path="/admin/analytics" element={<PlatformAnalyticsPage />} />
                <Route path="/advertise" element={<AdvertisePage />} />
                <Route path="/advertiser/dashboard" element={<AdvertiserDashboard />} />
                <Route path="/advertiser/campaigns/new" element={<AdvertiserNewCampaign />} />
                <Route path="/advertiser/campaigns/:id/edit" element={<AdvertiserNewCampaign />} />
                <Route path="/advertiser/checkout/success" element={<AdvertiserCheckoutSuccess />} />
                <Route path="/services/:category" element={<ServicesDirectoryPage />} />
                <Route path="/services/partner/:id" element={<PartnerDetailPage />} />
                <Route path="/partner/register" element={<PartnerRegisterPage />} />
                <Route path="/partner/dashboard" element={<PartnerDashboardPage />} />
                <Route path="/deal/:vehicleId" element={<DealCompletionPage />} />
                <Route path="/orders" element={<IndividualOnlyRoute><MyOrdersPage /></IndividualOnlyRoute>} />
                <Route path="/verify" element={<IndividualOnlyRoute><KycPage /></IndividualOnlyRoute>} />
                <Route path="/kyc" element={<IndividualOnlyRoute><KycPage /></IndividualOnlyRoute>} />
                <Route path="/messages" element={<IndividualOnlyRoute><MessagesPage /></IndividualOnlyRoute>} />
                <Route path="/favorites" element={<IndividualOnlyRoute><FavoritesPage /></IndividualOnlyRoute>} />
                <Route path="/saved-searches" element={<IndividualOnlyRoute><SavedSearchesPage /></IndividualOnlyRoute>} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/join" element={<JoinChooserPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/payment/success" element={<PaymentSuccessPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/disclaimer" element={<DisclaimerPage />} />
                <Route path="/refund-policy" element={<RefundPolicyPage />} />
                <Route path="/fraud-policy" element={<FraudPolicyPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </FavoritesProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
